import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  TrashIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import { useAuthContext } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { businessService, BusinessListing } from '../services/businessService';
import { aiService } from '../services/aiService';
import { SparklesIcon } from '@heroicons/react/24/outline';

const AdminListings: React.FC = () => {
  const { currentUser, userProfile } = useAuthContext();
  const [listings, setListings] = useState<BusinessListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterFranchise, setFilterFranchise] = useState<string>('all'); // 'all' | 'franchise' | 'non_franchise'
  const [filterEmptyDescription, setFilterEmptyDescription] = useState<string>('all'); // 'all' | 'empty' | 'has'
  const [filterEmptyRegion, setFilterEmptyRegion] = useState<string>('all'); // 'all' | 'empty' | 'has'
  const [filterMissingContent, setFilterMissingContent] = useState<string>('all'); // 'all' | 'ai_title' | 'seo_url' | 'categories' | 'buyer_types' | 'highlights'
  const [filterSource, setFilterSource] = useState<string>('all'); // 'all' | 'ddf' | 'user'
  const [sortField, setSortField] = useState<string>('basic.createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);
  const [aiLoading, setAiLoading] = useState<{
    titlesBatch: boolean;
    seoBatch: boolean;
    categoriesBatch: boolean;
    highlightsBatch: boolean;
    buyerTypesBatch: boolean;
    ddfSync: boolean;
    franchiseBackfill: boolean;
    ensureAiMetadata: boolean;
  }>({
    titlesBatch: false,
    seoBatch: false,
    categoriesBatch: false,
    highlightsBatch: false,
    buyerTypesBatch: false,
    ddfSync: false,
    franchiseBackfill: false,
    ensureAiMetadata: false,
  });
  const [rowAiLoadingId, setRowAiLoadingId] = useState<string | null>(null);
  const [openRowAiDropdownId, setOpenRowAiDropdownId] = useState<string | null>(null);
  const [aiToolsSection, setAiToolsSection] = useState<string | null>(null); // 'sync' | null
  const [selectedListingIds, setSelectedListingIds] = useState<string[]>([]);

  usePageTitle('Admin Listings | BizBuyMax.com');

  useEffect(() => {
    const loadListings = async () => {
      try {
        setLoading(true);
        const allBusinesses = await businessService.getAllBusinesses();
        setListings(allBusinesses);
      } catch (error) {
        console.error('Error loading listings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadListings();
  }, []);

  // Reset to page 1 when filters or items-per-page change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterCategory, filterFranchise, filterEmptyDescription, filterEmptyRegion, filterMissingContent, filterSource, itemsPerPage]);

  // Check if user is admin
  const isAdmin = currentUser && userProfile?.profile.userType === 'admin';

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">⚠️ Access Denied</div>
          <p className="text-gray-600 mb-4">You don't have permission to access the admin area.</p>
          <Link to="/" className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  // Filter and sort listings
  const filteredListings = listings
    .filter(listing => {
      const matchesSearch = 
        listing.basic.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (listing.basic.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        listing.location.city.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'all' || listing.basic.status === filterStatus;
      const matchesCategory = filterCategory === 'all' || listing.basic.category === filterCategory;
      const franchise = listing.business?.franchise ?? false;
      const matchesFranchise =
        filterFranchise === 'all' ||
        (filterFranchise === 'franchise' && franchise) ||
        (filterFranchise === 'non_franchise' && !franchise);
      const descEmpty = !(listing.basic.description || '').trim();
      const matchesEmptyDescription =
        filterEmptyDescription === 'all' ||
        (filterEmptyDescription === 'empty' && descEmpty) ||
        (filterEmptyDescription === 'has' && !descEmpty);
      const provinceEmpty = !(listing.location?.province || '').trim();
      const matchesEmptyRegion =
        filterEmptyRegion === 'all' ||
        (filterEmptyRegion === 'empty' && provinceEmpty) ||
        (filterEmptyRegion === 'has' && !provinceEmpty);

      const isDdf = !!listing.id && listing.id.startsWith('ddf-');
      const matchesSource =
        filterSource === 'all' ||
        (filterSource === 'ddf' && isDdf) ||
        (filterSource === 'user' && !isDdf);
      const missingAiTitle = listing.basic.aiMetadata?.titlesProcessed !== true;
      const missingSeoUrl = !(listing.basic.seoUrl && listing.basic.seoUrl.trim());
      const missingCategories = listing.basic.aiMetadata?.categoriesProcessed !== true;
      const missingBuyerTypes = listing.basic.aiMetadata?.buyerTypesProcessed !== true;
      const missingHighlights = listing.basic.aiMetadata?.highlightsProcessed !== true;
      const matchesMissingContent =
        filterMissingContent === 'all' ||
        (filterMissingContent === 'ai_title' && missingAiTitle) ||
        (filterMissingContent === 'seo_url' && missingSeoUrl) ||
        (filterMissingContent === 'categories' && missingCategories) ||
        (filterMissingContent === 'buyer_types' && missingBuyerTypes) ||
        (filterMissingContent === 'highlights' && missingHighlights);
      
      return (
        matchesSearch &&
        matchesStatus &&
        matchesCategory &&
        matchesFranchise &&
        matchesEmptyDescription &&
        matchesEmptyRegion &&
        matchesMissingContent &&
        matchesSource
      );
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'basic.title':
          aValue = a.basic.title;
          bValue = b.basic.title;
          break;
        case 'financial.askingPrice':
          aValue = a.financial.askingPrice;
          bValue = b.financial.askingPrice;
          break;
        case 'basic.createdAt':
          aValue = a.basic.createdAt;
          bValue = b.basic.createdAt;
          break;
        default:
          aValue = a.basic.createdAt;
          bValue = b.basic.createdAt;
      }
      // Normalize Firestore Timestamps for comparison (seconds or toMillis)
      const toNum = (v: any) => (v && typeof v.toMillis === 'function' ? v.toMillis() : (v?.seconds ?? (typeof v === 'object' && v !== null ? 0 : v)));
      if (sortField === 'basic.createdAt') {
        aValue = toNum(aValue);
        bValue = toNum(bValue);
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredListings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedListings = filteredListings.slice(startIndex, endIndex);

  const toggleSelectAllOnPage = () => {
    const pageIds = paginatedListings.map((l) => l.id!).filter(Boolean);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedListingIds.includes(id));
    if (allSelected) {
      setSelectedListingIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedListingIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const toggleSelectListing = (listingId: string) => {
    setSelectedListingIds((prev) =>
      prev.includes(listingId) ? prev.filter((id) => id !== listingId) : [...prev, listingId]
    );
  };
  
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDelete = async (listingId: string, listingTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete "${listingTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await businessService.deleteBusiness(listingId);
      // Remove from local state
      setListings(listings.filter(l => l.id !== listingId));
      alert('Listing deleted successfully');
    } catch (error) {
      console.error('Error deleting listing:', error);
      alert('Failed to delete listing. Please try again.');
    }
  };

  const rowAiTypeLabels: Record<'title' | 'categories' | 'highlights' | 'metadata' | 'buyerTypes', string> = {
    title: 'Title',
    categories: 'Categories',
    highlights: 'Highlights',
    metadata: 'SEO metadata',
    buyerTypes: 'Buyer types',
  };

  const handleRowAI = async (listing: BusinessListing, type: 'title' | 'categories' | 'highlights' | 'metadata' | 'buyerTypes') => {
    if (!listing.id) return;

    setOpenRowAiDropdownId(null);

    const label = rowAiTypeLabels[type];
    if (!window.confirm(`Generate AI ${label} for this listing and overwrite the existing value?`)) {
      return;
    }

    const isDdf = listing.id.startsWith('ddf-');
    const collectionName = isDdf ? 'ddfListings' : 'businesses';
    const listingId = isDdf ? listing.id.replace('ddf-', '') : listing.id;

    try {
      setRowAiLoadingId(listing.id);
      const result = await aiService.generateForListing({
        collectionName,
        listingId,
        type,
      });

      if (!result.success) {
        throw new Error(result.result?.error || 'AI generation failed');
      }

      alert(`AI ${label} generated successfully for this listing.`);

      const allBusinesses = await businessService.getAllBusinesses();
      setListings(allBusinesses);
    } catch (error: any) {
      console.error('Error generating AI for listing:', error);
      alert(`Failed to generate AI ${label} for this listing: ${error.message || 'Unknown error'}`);
    } finally {
      setRowAiLoadingId(null);
    }
  };

  const handleBatchGenerateAITitlesForSelected = async () => {
    if (selectedListingIds.length === 0) {
      alert('Please select at least one listing to generate AI titles.');
      return;
    }

    if (
      !window.confirm(
        `Generate AI titles for ${selectedListingIds.length} selected listings and overwrite existing titles if necessary?`
      )
    ) {
      return;
    }

    try {
      setAiLoading((prev) => ({ ...prev, titlesBatch: true }));

      let successCount = 0;
      let failCount = 0;
      const errors: string[] = [];

      for (const listingIdWithPrefix of selectedListingIds) {
        try {
          const isDdf = listingIdWithPrefix.startsWith('ddf-');
          const collectionName = isDdf ? 'ddfListings' : 'businesses';
          const listingId = isDdf ? listingIdWithPrefix.replace('ddf-', '') : listingIdWithPrefix;

          const result = await aiService.generateForListing({
            collectionName,
            listingId,
            type: 'title',
          });

          if (result.success) {
            successCount += 1;
          } else {
            failCount += 1;
            if (result.result?.error) {
              errors.push(result.result.error);
            }
          }
        } catch (err: any) {
          failCount += 1;
          errors.push(err?.message || 'Unknown error');
        }
      }

      let message = `AI Titles Batch Complete!\n\nSuccessful: ${successCount}`;
      if (failCount > 0) {
        message += `\nFailed: ${failCount}`;
      }
      if (errors.length > 0) {
        const sample = errors.slice(0, 5);
        message += `\n\nSample errors:\n${sample.map((e) => `- ${e}`).join('\n')}`;
      }

      alert(message);

      const allBusinesses = await businessService.getAllBusinesses();
      setListings(allBusinesses);
      setSelectedListingIds([]);
    } catch (error: any) {
      console.error('Error running batch AI titles:', error);
      alert(`Failed to run batch AI titles: ${error.message || 'Unknown error'}`);
    } finally {
      setAiLoading((prev) => ({ ...prev, titlesBatch: false }));
    }
  };

  const handleBatchGenerateSeoMetadataForSelected = async () => {
    if (selectedListingIds.length === 0) {
      alert('Please select at least one listing to generate SEO metadata.');
      return;
    }

    if (
      !window.confirm(
        `Generate SEO titles and URLs for ${selectedListingIds.length} selected listings and overwrite existing values if necessary?`
      )
    ) {
      return;
    }

    try {
      setAiLoading((prev) => ({ ...prev, seoBatch: true }));

      let successCount = 0;
      let failCount = 0;
      const errors: string[] = [];

      for (const listingIdWithPrefix of selectedListingIds) {
        try {
          const isDdf = listingIdWithPrefix.startsWith('ddf-');
          const collectionName = isDdf ? 'ddfListings' : 'businesses';
          const listingId = isDdf ? listingIdWithPrefix.replace('ddf-', '') : listingIdWithPrefix;

          const result = await aiService.generateForListing({
            collectionName,
            listingId,
            type: 'metadata',
          });

          if (result.success) {
            successCount += 1;
          } else {
            failCount += 1;
            if (result.result?.error) {
              errors.push(result.result.error);
            }
          }
        } catch (err: any) {
          failCount += 1;
          errors.push(err?.message || 'Unknown error');
        }
      }

      let message = `SEO Metadata Batch Complete!\n\nSuccessful: ${successCount}`;
      if (failCount > 0) {
        message += `\nFailed: ${failCount}`;
      }
      if (errors.length > 0) {
        const sample = errors.slice(0, 5);
        message += `\n\nSample errors:\n${sample.map((e) => `- ${e}`).join('\n')}`;
      }

      alert(message);

      const allBusinesses = await businessService.getAllBusinesses();
      setListings(allBusinesses);
      setSelectedListingIds([]);
    } catch (error: any) {
      console.error('Error running batch SEO metadata:', error);
      alert(`Failed to run batch SEO metadata: ${error.message || 'Unknown error'}`);
    } finally {
      setAiLoading((prev) => ({ ...prev, seoBatch: false }));
    }
  };

  const handleBatchGenerateAICategoriesForSelected = async () => {
    if (selectedListingIds.length === 0) {
      alert('Please select at least one listing to generate AI categories.');
      return;
    }
    if (!window.confirm(`Generate AI categories for ${selectedListingIds.length} selected listings and overwrite existing values?`)) return;
    try {
      setAiLoading((prev) => ({ ...prev, categoriesBatch: true }));
      let successCount = 0, failCount = 0;
      const errors: string[] = [];
      for (const id of selectedListingIds) {
        try {
          const isDdf = id.startsWith('ddf-');
          const collectionName = isDdf ? 'ddfListings' : 'businesses';
          const listingId = isDdf ? id.replace('ddf-', '') : id;
          const result = await aiService.generateForListing({ collectionName, listingId, type: 'categories' });
          if (result.success) successCount += 1;
          else { failCount += 1; if (result.result?.error) errors.push(result.result.error); }
        } catch (err: any) {
          failCount += 1;
          errors.push(err?.message || 'Unknown error');
        }
      }
      let msg = `AI Categories Batch Complete!\n\nSuccessful: ${successCount}`;
      if (failCount > 0) msg += `\nFailed: ${failCount}`;
      if (errors.length > 0) msg += `\n\nSample errors:\n${errors.slice(0, 5).map((e) => `- ${e}`).join('\n')}`;
      alert(msg);
      const allBusinesses = await businessService.getAllBusinesses();
      setListings(allBusinesses);
      setSelectedListingIds([]);
    } catch (error: any) {
      console.error('Error running batch AI categories:', error);
      alert(`Failed: ${error.message || 'Unknown error'}`);
    } finally {
      setAiLoading((prev) => ({ ...prev, categoriesBatch: false }));
    }
  };

  const handleBatchGenerateAIHighlightsForSelected = async () => {
    if (selectedListingIds.length === 0) {
      alert('Please select at least one listing to generate AI highlights.');
      return;
    }
    if (!window.confirm(`Generate AI highlights for ${selectedListingIds.length} selected listings and overwrite existing values?`)) return;
    try {
      setAiLoading((prev) => ({ ...prev, highlightsBatch: true }));
      let successCount = 0, failCount = 0;
      const errors: string[] = [];
      for (const id of selectedListingIds) {
        try {
          const isDdf = id.startsWith('ddf-');
          const collectionName = isDdf ? 'ddfListings' : 'businesses';
          const listingId = isDdf ? id.replace('ddf-', '') : id;
          const result = await aiService.generateForListing({ collectionName, listingId, type: 'highlights' });
          if (result.success) successCount += 1;
          else { failCount += 1; if (result.result?.error) errors.push(result.result.error); }
        } catch (err: any) {
          failCount += 1;
          errors.push(err?.message || 'Unknown error');
        }
      }
      let msg = `AI Highlights Batch Complete!\n\nSuccessful: ${successCount}`;
      if (failCount > 0) msg += `\nFailed: ${failCount}`;
      if (errors.length > 0) msg += `\n\nSample errors:\n${errors.slice(0, 5).map((e) => `- ${e}`).join('\n')}`;
      alert(msg);
      const allBusinesses = await businessService.getAllBusinesses();
      setListings(allBusinesses);
      setSelectedListingIds([]);
    } catch (error: any) {
      console.error('Error running batch AI highlights:', error);
      alert(`Failed: ${error.message || 'Unknown error'}`);
    } finally {
      setAiLoading((prev) => ({ ...prev, highlightsBatch: false }));
    }
  };

  const handleBatchGenerateAIBuyerTypesForSelected = async () => {
    if (selectedListingIds.length === 0) {
      alert('Please select at least one listing to generate AI buyer types.');
      return;
    }
    if (!window.confirm(`Generate AI buyer types for ${selectedListingIds.length} selected listings and overwrite existing values?`)) return;
    try {
      setAiLoading((prev) => ({ ...prev, buyerTypesBatch: true }));
      let successCount = 0, failCount = 0;
      const errors: string[] = [];
      for (const id of selectedListingIds) {
        try {
          const isDdf = id.startsWith('ddf-');
          const collectionName = isDdf ? 'ddfListings' : 'businesses';
          const listingId = isDdf ? id.replace('ddf-', '') : id;
          const result = await aiService.generateForListing({ collectionName, listingId, type: 'buyerTypes' });
          if (result.success) successCount += 1;
          else { failCount += 1; if (result.result?.error) errors.push(result.result.error); }
        } catch (err: any) {
          failCount += 1;
          errors.push(err?.message || 'Unknown error');
        }
      }
      let msg = `AI Buyer Types Batch Complete!\n\nSuccessful: ${successCount}`;
      if (failCount > 0) msg += `\nFailed: ${failCount}`;
      if (errors.length > 0) msg += `\n\nSample errors:\n${errors.slice(0, 5).map((e) => `- ${e}`).join('\n')}`;
      alert(msg);
      const allBusinesses = await businessService.getAllBusinesses();
      setListings(allBusinesses);
      setSelectedListingIds([]);
    } catch (error: any) {
      console.error('Error running batch AI buyer types:', error);
      alert(`Failed: ${error.message || 'Unknown error'}`);
    } finally {
      setAiLoading((prev) => ({ ...prev, buyerTypesBatch: false }));
    }
  };

  const handleSyncDDFListings = async () => {
    if (!window.confirm('This will sync all DDF listings from REALTOR.ca. This may take several minutes. Continue?')) {
      return;
    }

    try {
      setAiLoading(prev => ({ ...prev, ddfSync: true }));
      const result = await aiService.syncDDFListings();
      
      const stats = result.stats;
      let message = `DDF Sync Complete!\n\nNew: ${stats.new}\nUpdated: ${stats.updated}\nUnchanged: ${stats.unchanged}\nDeactivated: ${stats.deactivated}`;
      if (stats.errors > 0) {
        message += `\nErrors: ${stats.errors}`;
      }
      
      alert(message);
      
      // Reload listings to show updates
      const allBusinesses = await businessService.getAllBusinesses();
      setListings(allBusinesses);
    } catch (error: any) {
      console.error('Error syncing DDF listings:', error);
      alert(`Error syncing DDF listings: ${error.message || 'Unknown error'}`);
    } finally {
      setAiLoading(prev => ({ ...prev, ddfSync: false }));
    }
  };

  const handleFranchiseBackfill = async () => {
    if (!window.confirm(
      'Run franchise backfill?\n\n' +
      'This will scan all ddfListings, set business.franchise from description/subcategory/tags containing "franchise", and sync the franchises collection. Continue?'
    )) {
      return;
    }
    try {
      setAiLoading(prev => ({ ...prev, franchiseBackfill: true }));
      const functions = getFunctions(getApp());
      const backfillFn = httpsCallable<unknown, { success: boolean; updated: number; addedToFranchises: number; removedFromFranchises: number; errors: number; message: string }>(functions, 'backfillDDFFranchiseFlags');
      const res = await backfillFn({});
      const data = res.data;
      if (data?.success) {
        alert(
          `Franchise backfill complete.\n\n` +
          `Updated: ${data.updated}\n` +
          `Added to franchises: ${data.addedToFranchises}\n` +
          `Removed from franchises: ${data.removedFromFranchises}\n` +
          (data.errors > 0 ? `Errors: ${data.errors}` : '')
        );
        const allBusinesses = await businessService.getAllBusinesses();
        setListings(allBusinesses);
      } else {
        const partial = [data?.updated, data?.addedToFranchises, data?.removedFromFranchises, data?.errors]
          .filter((n) => n != null)
          .every((n) => n === 0);
        alert(
          partial
            ? (data?.message || 'Backfill failed.')
            : `Backfill stopped: ${data?.updated} updated, ${data?.addedToFranchises} added, ${data?.removedFromFranchises} removed, ${data?.errors} errors.\n\n${data?.message || ''}`
        );
        if ((data?.updated ?? 0) > 0 || (data?.addedToFranchises ?? 0) > 0) {
          const allBusinesses = await businessService.getAllBusinesses();
          setListings(allBusinesses);
        }
      }
    } catch (error: any) {
      console.error('Error running franchise backfill:', error);
      alert(`Franchise backfill failed: ${error.message || 'Unknown error'}`);
    } finally {
      setAiLoading(prev => ({ ...prev, franchiseBackfill: false }));
    }
  };

  const handleEnsureDDFAiMetadata = async () => {
    if (!window.confirm(
      'Ensure AI metadata on all ddfListings?\n\n' +
      'This will add basic.aiMetadata (titlesProcessed, etc.) to any ddfListing that is missing it, so they appear in the "Create AI Titles" query. Run this once for existing listings that never got titles, then run "Create AI Titles". Continue?'
    )) {
      return;
    }
    try {
      setAiLoading(prev => ({ ...prev, ensureAiMetadata: true }));
      const functions = getFunctions(getApp());
      const ensureFn = httpsCallable<unknown, { success: boolean; updated: number; errors: number; message: string }>(functions, 'ensureDDFAiMetadataFields');
      const res = await ensureFn({});
      const data = res.data;
      if (data?.success) {
        alert(
          `Ensure AI metadata complete.\n\nUpdated: ${data.updated}` +
          (data.errors > 0 ? `\nErrors: ${data.errors}` : '') +
          (data.updated > 0 ? '\n\nRun "Create AI Titles" next to generate titles for those listings.' : '')
        );
        if ((data?.updated ?? 0) > 0) {
          const allBusinesses = await businessService.getAllBusinesses();
          setListings(allBusinesses);
        }
      } else {
        alert(data?.message ?? 'Ensure AI metadata failed.');
      }
    } catch (error: any) {
      console.error('Error ensuring DDF AI metadata:', error);
      alert(`Ensure AI metadata failed: ${error.message || 'Unknown error'}`);
    } finally {
      setAiLoading(prev => ({ ...prev, ensureAiMetadata: false }));
    }
  };

  const handleBulkDeleteSelected = async () => {
    if (selectedListingIds.length === 0) {
      alert('Please select at least one listing to delete.');
      return;
    }

    const selectedCount = selectedListingIds.length;
    const confirmMessage =
      selectedCount === 1
        ? 'Delete the selected listing? This action cannot be undone.'
        : `Delete ${selectedCount} selected listings? This action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      let successCount = 0;
      let failCount = 0;
      const errors: string[] = [];

      for (const id of selectedListingIds) {
        try {
          await businessService.deleteBusiness(id);
          successCount += 1;
        } catch (err: any) {
          failCount += 1;
          errors.push(err?.message || 'Unknown error');
        }
      }

      let message = `Bulk delete complete.\n\nDeleted: ${successCount}`;
      if (failCount > 0) {
        message += `\nFailed: ${failCount}`;
      }
      if (errors.length > 0) {
        const sample = errors.slice(0, 5);
        message += `\n\nSample errors:\n${sample.map((e) => `- ${e}`).join('\n')}`;
      }

      alert(message);

      const allBusinesses = await businessService.getAllBusinesses();
      setListings(allBusinesses);
      setSelectedListingIds([]);
    } catch (error: any) {
      console.error('Error performing bulk delete:', error);
      alert(`Bulk delete failed: ${error.message || 'Unknown error'}`);
    }
  };

  const truncateTitle = (title: string, maxLength: number = 100) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + '...';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'sold': return 'bg-blue-100 text-blue-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-purple-100 text-purple-800',
      'bg-yellow-100 text-yellow-800',
      'bg-red-100 text-red-800',
      'bg-indigo-100 text-indigo-800'
    ];
    const index = category.length % colors.length;
    return colors[index];
  };

  const getPlanName = (listing: BusinessListing): string | null => {
    const pkg = (listing as any).package;
    if (!pkg) return null;
    return pkg.name || pkg.id || null;
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '—';
    try {
      const date = typeof timestamp.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  // Get unique categories for filter
  const categories = Array.from(new Set(listings.map(l => l.basic.category))).sort();

  return (
    <>
      <Helmet>
        <title>Admin Listings | BizBuyMax.com</title>
        <meta name="description" content="Manage business listings in the admin dashboard" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Manage Listings</h1>
                <p className="text-gray-600 mt-1">View and manage all business listings</p>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to="/sell-business"
                  className="bg-secondary-500 hover:bg-secondary-600 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  Create Listing
                </Link>
                <Link
                  to="/admin/dashboard"
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  Back to Dashboard
                </Link>
              </div>
            </div>

            {/* AI & sync tools accordion */}
            <div className="mt-4 border-t border-gray-200 pt-4">
              <div className="text-sm font-medium text-gray-700 mb-2">AI &amp; sync tools</div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setAiToolsSection(aiToolsSection === 'sync' ? null : 'sync')}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left font-medium text-gray-900"
                >
                  <span>DDF sync &amp; backfills</span>
                  {aiToolsSection === 'sync' ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                </button>
                {aiToolsSection === 'sync' && (
                  <div className="px-4 py-3 bg-white border-t border-gray-200">
                    <p className="text-sm text-gray-600 mb-3">Use after new DDF feed, first-time franchise tagging, or when older listings lack AI metadata.</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleSyncDDFListings}
                        disabled={aiLoading.ddfSync}
                        className="bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white px-4 py-2 rounded-md font-medium text-sm flex items-center gap-2"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {aiLoading.ddfSync ? 'Syncing...' : 'Sync DDF Listings'}
                      </button>
                      <button
                        onClick={handleFranchiseBackfill}
                        disabled={aiLoading.franchiseBackfill}
                        className="bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white px-4 py-2 rounded-md font-medium text-sm flex items-center gap-2"
                      >
                        <BuildingStorefrontIcon className="h-4 w-4" />
                        {aiLoading.franchiseBackfill ? 'Running...' : 'Run Franchise Backfill'}
                      </button>
                      <button
                        onClick={handleEnsureDDFAiMetadata}
                        disabled={aiLoading.ensureAiMetadata}
                        className="bg-slate-600 hover:bg-slate-700 disabled:bg-slate-400 text-white px-4 py-2 rounded-md font-medium text-sm flex items-center gap-2"
                      >
                        <SparklesIcon className="h-4 w-4" />
                        {aiLoading.ensureAiMetadata ? 'Ensuring...' : 'Ensure AI Metadata'}
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filters and bulk actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2 md:gap-3">
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search title, description, city..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64 max-w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">Status: All</option>
                  <option value="active">Status: Active</option>
                  <option value="pending">Status: Pending</option>
                  <option value="sold">Status: Sold</option>
                  <option value="expired">Status: Expired</option>
                  <option value="draft">Status: Draft</option>
                </select>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">Category: All</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <select
                  value={filterFranchise}
                  onChange={(e) => setFilterFranchise(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">Franchise: All</option>
                  <option value="franchise">Franchise only</option>
                  <option value="non_franchise">Non-franchise only</option>
                </select>
              </div>
              <div className="flex flex-wrap items-center gap-2 md:gap-3">
                <select
                  value={filterEmptyDescription}
                  onChange={(e) => setFilterEmptyDescription(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">Description: All</option>
                  <option value="empty">Description: Empty</option>
                  <option value="has">Description: Has content</option>
                </select>
                <select
                  value={filterEmptyRegion}
                  onChange={(e) => setFilterEmptyRegion(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">Region: All</option>
                  <option value="empty">Region: Missing</option>
                  <option value="has">Region: Present</option>
                </select>
                <select
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">Source: All</option>
                  <option value="ddf">Source: DDF only</option>
                  <option value="user">Source: User-submitted only</option>
                </select>
                <select
                  value={filterMissingContent}
                  onChange={(e) => setFilterMissingContent(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">Missing content: All</option>
                  <option value="ai_title">Missing AI titles</option>
                  <option value="seo_url">Missing SEO URLs</option>
                  <option value="categories">Missing Categories</option>
                  <option value="buyer_types">Missing Buyer types</option>
                  <option value="highlights">Missing Highlights</option>
                </select>
                <select
                  value={`${sortField}|${sortDirection}`}
                  onChange={(e) => {
                    const [field, dir] = e.target.value.split('|') as [string, 'asc' | 'desc'];
                    setSortField(field);
                    setSortDirection(dir);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="basic.createdAt|desc">Sort: Newest imported</option>
                  <option value="basic.createdAt|asc">Sort: Oldest imported</option>
                  <option value="basic.title|asc">Sort: Title A–Z</option>
                  <option value="basic.title|desc">Sort: Title Z–A</option>
                  <option value="financial.askingPrice|desc">Sort: Price High–Low</option>
                  <option value="financial.askingPrice|asc">Sort: Price Low–High</option>
                </select>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm">
              <div className="flex flex-wrap items-center gap-3 text-gray-600">
                <span>
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredListings.length)} of {filteredListings.length} listings
                  {filteredListings.length !== listings.length && ` (${listings.length} total)`}
                </span>
                <label className="flex items-center gap-2">
                  <span>Per page</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={250}>250</option>
                    <option value={500}>500</option>
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-gray-600 mr-2">
                  {selectedListingIds.length > 0
                    ? `${selectedListingIds.length} selected`
                    : 'No listings selected'}
                </span>
                <button
                  type="button"
                  onClick={handleBatchGenerateAITitlesForSelected}
                  disabled={selectedListingIds.length === 0 || aiLoading.titlesBatch}
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed"
                >
                  <SparklesIcon className="h-4 w-4 mr-1" />
                  {aiLoading.titlesBatch ? 'AI titles…' : 'AI titles'}
                </button>
                <button
                  type="button"
                  onClick={handleBatchGenerateSeoMetadataForSelected}
                  disabled={selectedListingIds.length === 0 || aiLoading.seoBatch}
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed"
                >
                  <SparklesIcon className="h-4 w-4 mr-1" />
                  {aiLoading.seoBatch ? 'SEO metadata…' : 'SEO metadata'}
                </button>
                <button
                  type="button"
                  onClick={handleBatchGenerateAICategoriesForSelected}
                  disabled={selectedListingIds.length === 0 || aiLoading.categoriesBatch}
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
                >
                  <SparklesIcon className="h-4 w-4 mr-1" />
                  {aiLoading.categoriesBatch ? 'Categories…' : 'Categories'}
                </button>
                <button
                  type="button"
                  onClick={handleBatchGenerateAIHighlightsForSelected}
                  disabled={selectedListingIds.length === 0 || aiLoading.highlightsBatch}
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed"
                >
                  <SparklesIcon className="h-4 w-4 mr-1" />
                  {aiLoading.highlightsBatch ? 'Highlights…' : 'Highlights'}
                </button>
                <button
                  type="button"
                  onClick={handleBatchGenerateAIBuyerTypesForSelected}
                  disabled={selectedListingIds.length === 0 || aiLoading.buyerTypesBatch}
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 disabled:cursor-not-allowed"
                >
                  <SparklesIcon className="h-4 w-4 mr-1" />
                  {aiLoading.buyerTypesBatch ? 'Buyer types…' : 'Buyer types'}
                </button>
                <button
                  type="button"
                  onClick={handleBulkDeleteSelected}
                  disabled={selectedListingIds.length === 0}
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Delete selected
                </button>
              </div>
            </div>
          </div>

          {/* Listings Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading listings...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                          onChange={toggleSelectAllOnPage}
                          checked={
                            paginatedListings.length > 0 &&
                            paginatedListings.every(
                              (l) => l.id && selectedListingIds.includes(l.id)
                            )
                          }
                          aria-label="Select all listings on this page"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('basic.title')}>
                        <div className="flex items-center space-x-1">
                          <span>Listing</span>
                          {sortField === 'basic.title' && (
                            sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('financial.askingPrice')}>
                        <div className="flex items-center space-x-1">
                          <span>Price</span>
                          {sortField === 'financial.askingPrice' && (
                            sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('basic.createdAt')}>
                        <div className="flex items-center space-x-1">
                          <span>Dates</span>
                          {sortField === 'basic.createdAt' && (
                            sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedListings.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                          No listings found matching your filters.
                        </td>
                      </tr>
                    ) : (
                      paginatedListings.map((listing) => (
                      <tr key={listing.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 align-top">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded mt-1"
                            checked={!!(listing.id && selectedListingIds.includes(listing.id))}
                            onChange={() => listing.id && toggleSelectListing(listing.id)}
                            aria-label="Select listing"
                          />
                        </td>
                        <td className="px-6 py-3 min-w-0 max-w-md">
                          <div className="space-y-1.5">
                            <div className="font-medium text-gray-900 leading-tight" title={listing.basic.title}>
                              {truncateTitle(listing.basic.title, 90)}
                            </div>
                            {(listing.basic.shortDescription || listing.basic.description) && (
                              <p className="text-sm text-gray-500 line-clamp-2 leading-snug">
                                {listing.basic.shortDescription || truncateTitle(listing.basic.description || '', 200)}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getCategoryColor(listing.basic.category)}`}>
                                {listing.basic.category}
                              </span>
                              <span className="inline-flex items-center text-xs text-gray-500">
                                <MapPinIcon className="h-3.5 w-3.5 mr-1 text-gray-400 flex-shrink-0" />
                                {listing.location.city}, {listing.location.province}
                              </span>
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(listing.basic.status)}`}>
                                {listing.basic.status.charAt(0).toUpperCase() + listing.basic.status.slice(1)}
                              </span>
                              {listing.basic.featured && (
                                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                                  Featured
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-1.5 pt-1.5 border-t border-gray-100">
                              <span className="font-medium text-gray-700">{listing.owner.contactInfo.name}</span>
                              <span className="mx-1.5">·</span>
                              <span>{listing.owner.contactInfo.email}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap align-top">
                          <div className="flex items-center text-sm text-gray-900 font-medium">
                            <CurrencyDollarIcon className="h-4 w-4 mr-1 text-gray-400 flex-shrink-0" />
                            ${listing.financial.askingPrice.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 align-top">
                          <div className="flex flex-col space-y-0.5">
                            <span>
                              <span className="font-medium text-gray-700">Created:</span>{' '}
                              {formatDate(listing.basic.createdAt)}
                            </span>
                            {!listing.id?.startsWith('ddf-') && listing.basic.expiresAt && (
                              <span>
                                <span className="font-medium text-gray-700">Expires:</span>{' '}
                                {formatDate(listing.basic.expiresAt)}
                              </span>
                            )}
                            {!listing.id?.startsWith('ddf-') && getPlanName(listing) && (
                              <span>
                                <span className="font-medium text-gray-700">Plan:</span>{' '}
                                {getPlanName(listing)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 align-top">
                          <div className="flex flex-col gap-1.5 min-w-[7rem]">
                            <Link
                              to={listing.basic.seoUrl ? `/business/${listing.basic.seoUrl}` : `/business/${listing.id}`}
                              className="inline-flex items-center gap-2 px-2 py-1.5 rounded text-sm text-primary-600 hover:bg-primary-50 hover:text-primary-800"
                              title="View listing"
                            >
                              <EyeIcon className="h-4 w-4 flex-shrink-0" />
                              <span>View</span>
                            </Link>
                            <Link
                              to={`/admin/listings/${listing.id}/edit`}
                              className="inline-flex items-center gap-2 px-2 py-1.5 rounded text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                              title="Edit listing"
                            >
                              <PencilIcon className="h-4 w-4 flex-shrink-0" />
                              <span>Edit</span>
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleDelete(listing.id!, listing.basic.title)}
                              className="inline-flex items-center gap-2 px-2 py-1.5 rounded text-sm text-red-600 hover:bg-red-50 hover:text-red-800 text-left"
                              title="Delete listing"
                            >
                              <TrashIcon className="h-4 w-4 flex-shrink-0" />
                              <span>Delete</span>
                            </button>
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setOpenRowAiDropdownId(openRowAiDropdownId === listing.id ? null : listing.id!)}
                                disabled={rowAiLoadingId === listing.id}
                                className="inline-flex items-center gap-2 px-2 py-1.5 rounded text-sm text-purple-600 hover:bg-purple-50 hover:text-purple-800 disabled:opacity-50 disabled:cursor-not-allowed w-full text-left"
                                title="Overwrite listing elements with AI"
                              >
                                <SparklesIcon className="h-4 w-4 flex-shrink-0" />
                                <span>AI</span>
                                <ChevronDownIcon className="h-3.5 w-3.5 ml-auto flex-shrink-0" />
                              </button>
                              {openRowAiDropdownId === listing.id && (
                                <>
                                  <div
                                    className="fixed inset-0 z-10"
                                    aria-hidden="true"
                                    onClick={() => setOpenRowAiDropdownId(null)}
                                  />
                                  <div className="absolute right-0 z-20 mt-1 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    {(['title', 'categories', 'highlights', 'metadata', 'buyerTypes'] as const).map((type) => (
                                      <button
                                        key={type}
                                        type="button"
                                        onClick={() => handleRowAI(listing, type)}
                                        className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-900"
                                      >
                                        Overwrite {rowAiTypeLabels[type]}
                                      </button>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination Controls */}
            {!loading && filteredListings.length > itemsPerPage && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(endIndex, filteredListings.length)}</span> of{' '}
                      <span className="font-medium">{filteredListings.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronDownIcon className="h-5 w-5 rotate-90" aria-hidden="true" />
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronDownIcon className="h-5 w-5 -rotate-90" aria-hidden="true" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminListings; 